"use client";

import { Body, Box, DuoCon, FramedImage, Heading, Stack } from "@biom3/react";
import { appConfig } from "@constants";
import Image from "next/image";
import type { ReactNode } from "react";

type LogoConfig = {
  src: string;
  alt: string;
};

export const IMMUTABLE_LOGO: LogoConfig = {
  src: `${appConfig.ASSET_BASE_URL}/images/quests/immutable-logo.svg`,
  alt: "Immutable logo",
};

export const IMMUTABLE_OG_IMAGE: LogoConfig = {
  src: `${appConfig.ASSET_BASE_URL}/images/openGraphImage.png`,
  alt: "Immutable logo",
};

type RedirectLayoutProps = {
  title: string;
  message?: string | ReactNode;
  leftLogo: LogoConfig;
  rightLogo?: LogoConfig;
  showSuccessState?: boolean;
  children?: ReactNode;
};

export const RedirectLayout = ({
  title,
  message,
  leftLogo,
  rightLogo,
  showSuccessState: showTickBadge = false,
  children,
}: RedirectLayoutProps) => {
  return (
    <Box
      sx={{
        backgroundColor: "base.color.neutral.900",
        width: "100vw",
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <Box
        sx={{
          position: "relative",
          width: "100%",
          height: "293px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          mb: "base.spacing.x4",
        }}
      >
        <Image
          id="lens-flare-background"
          src="/images/quests/lens-flare-background.png"
          alt="Lens flare background"
          width={1096}
          height={317}
          style={{
            position: "absolute",
            objectFit: "cover",
            maxWidth: "100vw",
            maxHeight: "100vh",
          }}
        />
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="center"
          gap="base.spacing.x4"
          sx={{
            position: "relative",
          }}
        >
          {!showTickBadge && (
            <>
              <FramedImage
                imageUrl={leftLogo.src}
                alt={leftLogo.alt}
                relativeImageSizeInLayout="64px"
                emphasized={true}
                objectFit="contain"
                sx={{
                  width: "64px",
                  height: "64px",
                  padding: "base.spacing.x1",
                }}
              />
              {rightLogo && (
                <Image
                  id="double-sided-arrow"
                  src="/images/quests/double-sided-arrow.svg"
                  alt="Double sided arrow"
                  width={26}
                  height={8}
                />
              )}
            </>
          )}
          {rightLogo && (
            <FramedImage
              imageUrl={rightLogo.src}
              alt={rightLogo.alt}
              relativeImageSizeInLayout="64px"
              emphasized={true}
              objectFit="contain"
              sx={{
                width: "64px",
                height: "64px",
                padding: "base.spacing.x1",
              }}
            />
          )}
          {showTickBadge && (
            <>
              <Box
                sx={{
                  width: "28px",
                  height: "28px",
                  position: "absolute",
                  right: "-6px",
                  bottom: "-6px",
                  borderRadius: "50%",
                  backgroundColor: "black",
                }}
              />
              <DuoCon
                icon="Tick"
                variant="bold"
                iconVariant="bold"
                outerShape="circle"
                colorVariant="success"
                sx={{
                  width: "24px",
                  height: "24px",
                  position: "absolute",
                  right: "-4px",
                  bottom: "-4px",
                }}
              />
            </>
          )}
        </Stack>
      </Box>

      <Heading
        sx={{
          maxWidth: "430px",
          textAlign: "center",
          px: "base.spacing.x4",
        }}
      >
        {title}
      </Heading>
      {message && (
        <Body
          sx={{
            maxWidth: "430px",
            textAlign: "center",
            mt: "base.spacing.x4",
            px: "base.spacing.x4",
          }}
        >
          {message}
        </Body>
      )}
      {children}
    </Box>
  );
};
